// The competitiveness block — the Google-defining view.
//
// Search impression share split into WON / LOST TO BUDGET / LOST TO RANK. That
// split is the whole point: "we're getting 20% of the auction" is an
// observation, but "…and 29% of what we lost was budget, 52% was rank" is an
// action — one says raise the budget, the other says fix bids or quality score.
//
// ⚠️ NEVER RENDERS A ZERO IT DIDN'T MEASURE. Google writes 0.0 rather than NULL
// where a share does not apply (a PMAX campaign's YouTube rows report 0% search
// IS; 170 live campaign-days report 0% click share while carrying 1,719 clicks).
// The view layer nulls those, and an unmeasured value shows "—" with a reason.
// A LOST-share of 0 is different and IS shown as 0% — "losing nothing to budget"
// is a real, useful measurement.

import { Info } from 'lucide-react';
import { cn } from '../../lib/utils';
import { NOT_MEASURED, formatPct } from './metricFormat';
import type { GoogleAdsMetrics } from '../../types/googleAdsReporting';

/** One segment of the won/lost bar. */
interface Segment {
  label: string;
  value: number | null;
  className: string;
  hint: string;
}

export function CompetitivenessBlock({
  metrics,
  loading,
}: {
  metrics: GoogleAdsMetrics | undefined;
  loading: boolean;
}): React.JSX.Element {
  const won = metrics?.searchImpressionShare ?? null;
  const lostBudget = metrics?.searchBudgetLostImpressionShare ?? null;
  const lostRank = metrics?.searchRankLostImpressionShare ?? null;

  const segments: Segment[] = [
    {
      label: 'Won',
      value: won,
      className: 'bg-kyck-500',
      hint: 'Impressions you won as a share of those you were eligible for',
    },
    {
      label: 'Lost — budget',
      value: lostBudget,
      className: 'bg-amber-500',
      hint: 'Eligible impressions lost because the budget ran out — raise budget to recover',
    },
    {
      label: 'Lost — rank',
      value: lostRank,
      className: 'bg-rose-500',
      hint: 'Eligible impressions lost to Ad Rank (bid + quality) — improve bids or quality',
    },
  ];

  const measured = segments.some((s) => s.value !== null);
  // Google's own rounding + its ">90%" sentinel make the three sum slightly over
  // 100, so the bar is normalised for DISPLAY only; the printed figures stay the
  // real measured values.
  const total = segments.reduce((n, s) => n + (s.value ?? 0), 0);
  const scale = total > 0 ? 100 / total : 0;

  return (
    <section className="rounded-lg border border-border bg-card p-4">
      <header className="mb-3 flex items-center gap-2">
        <h2 className="text-sm font-semibold">Auction competitiveness</h2>
        <span
          className="text-muted-foreground"
          title="Search-network impression share. Won + lost-to-budget + lost-to-rank account for every impression you were eligible for."
        >
          <Info className="h-3.5 w-3.5" />
        </span>
      </header>

      {loading && !metrics ? (
        <div className="h-4 w-full animate-pulse rounded bg-muted" />
      ) : !measured ? (
        <p className="text-sm text-muted-foreground">
          {NOT_MEASURED} Impression share isn’t reported for this scope. Google publishes it for
          Search-network delivery only, so a selection with no Search campaigns has none.
        </p>
      ) : (
        <>
          <div className="flex h-3 w-full overflow-hidden rounded-full bg-muted">
            {segments.map((s) =>
              s.value === null ? null : (
                <div
                  key={s.label}
                  className={cn('h-full', s.className)}
                  style={{ width: `${s.value * scale}%` }}
                  title={`${s.label}: ${formatPct(s.value)}`}
                />
              ),
            )}
          </div>
          <div className="mt-3 grid grid-cols-3 gap-3">
            {segments.map((s) => (
              <div key={s.label} title={s.hint}>
                <div className="flex items-center gap-1.5">
                  <span className={cn('h-2 w-2 rounded-full', s.className)} />
                  <span className="text-xs text-muted-foreground">{s.label}</span>
                </div>
                <div
                  className={cn(
                    'mt-0.5 text-lg font-semibold tabular-nums',
                    s.value === null && 'text-muted-foreground',
                  )}
                >
                  {s.value === null ? NOT_MEASURED : formatPct(s.value)}
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      <div className="mt-4 grid grid-cols-2 gap-3 border-t border-border pt-3 sm:grid-cols-4">
        <Secondary label="Top IS" value={metrics?.searchTopImpressionShare ?? null} />
        <Secondary label="Abs. top IS" value={metrics?.searchAbsoluteTopImpressionShare ?? null} />
        <Secondary
          label="Click share"
          value={metrics?.searchClickShare ?? null}
          note="Google reports click share only for some campaigns"
        />
        <Secondary
          label="Content IS"
          value={metrics?.contentImpressionShare ?? null}
          note="Display-network only"
        />
      </div>
    </section>
  );
}

function Secondary({
  label,
  value,
  note,
}: {
  label: string;
  value: number | null;
  note?: string;
}): React.JSX.Element {
  return (
    <div title={value === null ? note : undefined}>
      <div className="text-xs text-muted-foreground">{label}</div>
      <div
        className={cn(
          'mt-0.5 text-base font-medium tabular-nums',
          value === null && 'text-muted-foreground',
        )}
      >
        {value === null ? NOT_MEASURED : formatPct(value)}
      </div>
    </div>
  );
}
